<template>
  <div>
    <Modal
      :title="modalTitle"
      :saving="isSaving"
      data-cy="coverage-area-modal"
      @hide-modal="handleHide"
      @submit="handleSave"
      @delete="handleDelete"
    >
      <template #delete>
        <div>
          <Button
            v-if="isEditing"
            data-cy="deleteButton"
            type="danger"
            text="Delete"
            @click="handleDelete()"
          />
        </div>
      </template>
      <AddressEditor v-model="address" hide-street :v$="v$" />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import pick from "lodash/pick";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Button from "@/components/Button.vue";
import AddressEditor from "@/CompanyCoverageAreas/AddressEditor.vue";
import { CoverageArea } from "@/typings/company";
import useVuelidate from "@vuelidate/core";
import { helpers, maxLength, required } from "@vuelidate/validators";
import Modal from "../components/Modal.vue";

const props = withDefaults(
  defineProps<{
    coverageArea: CoverageArea;
  }>(),
  {
    coverageArea: null
  }
);

const emit = defineEmits(["save-success", "hide-modal"]);

const store = useStore();

const zipCode = ref("");
const city = ref("");
const state = ref("");

const isSaving = ref(false);
const isEditing = ref(false);

const modalTitle = computed(() => {
  return isEditing.value ? "Edit Coverage Area" : "Create Coverage Area";
});

const address = ref({});

onMounted(() => {
  if (props.coverageArea?.id) {
    isEditing.value = true;
    zipCode.value = props.coverageArea.zip_code;
    city.value = props.coverageArea.city;
    state.value = props.coverageArea.state;

    address.value = {
      city: props.coverageArea.city,
      state: props.coverageArea.state,
      zip_code: props.coverageArea.zip_code
    };
  }
});

const { withMessage } = helpers;
const serverErrors = ref({});

function hasServerError(fieldName) {
  return !Object.keys(serverErrors.value).includes(fieldName);
}

const v$ = useVuelidate(
  {
    zip_code: {
      required: withMessage("Zip Code is required", required),
      length: maxLength(10),
      serverErrors: withMessage(
        () => serverErrors.value?.zip_code ?? "default",
        () => hasServerError("zip_code")
      )
    }
  },
  {
    zip_code: computed(() => {
      return address.value.zip_code;
    })
  }
);

async function confirmDelete() {
  const payload = {
    message: "Are you sure you want to delete this coverage area?",
    title: "Confirm Delete Coverage Area"
  };
  return store.dispatch("confirmDelete", payload);
}

async function handleSave() {
  serverErrors.value = {};
  v$.value.$reset();

  const valid = await v$.value.$validate();

  if (!valid) {
    return;
  }

  isSaving.value = true;
  const action = isEditing.value ? "updateCoverageArea" : "createCoverageArea";
  const payload = {
    coverage_area: {
      id: props.coverageArea.id,
      ...pick(address.value, "city", "state", "zip_code")
    }
  };

  store
    .dispatch(action, payload)
    .then(() => {
      const actionName = isEditing.value ? "edited" : "created";
      closeModalWithSuccess(actionName);
    })
    .catch((ex) => {
      serverErrors.value = ex.response.data;

      v$.value.$reset();
      v$.value.$touch();

      isSaving.value = false;
    });
}

function closeModalWithSuccess(action: string) {
  window.notify.success(`Coverage Area has been ${action} successfully!`);
  isSaving.value = false;
  emit("save-success");
  emit("hide-modal");
}

function handleHide() {
  emit("hide-modal");
}

function handleDelete() {
  confirmDelete().then(
    () => {
      store
        .dispatch("deleteCoverageArea", props.coverageArea.id)
        .then(() => {
          window.notify.success("Coverage Area deleted successfully!");
          emit("save-success");
        })
        .finally(() => {
          emit("hide-modal");
        });
    },
    () => {}
  );
}
</script>

<style lang="scss" scoped></style>
